import { Box, Typography } from '@mui/material';
import {
  rewardInfoButtonSrc,
  medalRank1Src,
  medalRank2Src,
  medalRank3Src,
  rapwheelIconSrc,
  rewardCouponIconSrc,
  scoreIconSrc,
  streakIconSrc,
  timerIconSrc,
} from '../../assets/my-rewards';

const MEDAL_BY_RANK = {
  1: medalRank1Src,
  2: medalRank2Src,
  3: medalRank3Src,
};

const CARD_THEMES = {
  welcome: {
    outerGradient: 'linear-gradient(90deg, #e323d0 1%, #fc33e8 99.125%)',
    outerBorder: '#d62fc0',
    innerBg: '#ff4bde',
    innerBorder: '#a708aa',
    headerBorder: '#6e005a',
  },
  dailyScore: {
    outerGradient: 'linear-gradient(90deg, #ff534b 1%, #ff6a62 99.125%)',
    outerBorder: '#aa0808',
    innerBg: '#ff534b',
    innerBorder: '#aa0808',
    headerBorder: '#6e0500',
  },
  gameTime: {
    outerGradient: 'linear-gradient(90deg, #e3dd23 1%, #fcd733 99.125%)',
    outerBorder: '#d6a72f',
    innerBg: '#f2c739',
    innerBorder: '#7f6e00',
    headerBorder: '#6e5a00',
  },
  dailyStreak: {
    outerGradient: 'linear-gradient(90deg, #ace2ff 1%, #318cbe 99.125%)',
    outerBorder: '#2fd6cb',
    innerBg: '#76cfff',
    innerBorder: '#006a7f',
    headerBorder: '#00516e',
  },
};

const LEFT_VARIANTS = {
  wheel: {
    icon: rapwheelIconSrc,
    iconBox: 25,
    imageSx: { width: '118.69%', height: '124.58%', left: '-7.07%', top: '-6.82%' },
    labelKey: 'leftLabel',
  },
  score: {
    icon: scoreIconSrc,
    iconBox: 25,
    imageSx: { width: '137.45%', height: '137.45%', left: '-18.79%', top: '-15.97%' },
    labelKey: 'leftValue',
  },
  timer: {
    icon: timerIconSrc,
    iconBox: 25,
    imageSx: { width: '446.51%', height: '297.67%', left: '-321.18%', top: '-95.4%' },
    labelKey: 'leftValue',
  },
  streak: {
    icon: streakIconSrc,
    iconBox: 24,
    imageSx: { width: '100%', height: '100%', left: 0, top: 0, objectFit: 'cover' },
    labelKey: 'leftValue',
  },
};

const pillSx = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  minHeight: 38,
  px: 1.5,
  borderRadius: '8px',
  bgcolor: 'rgba(0, 0, 0, 0.54)',
  boxShadow: 'inset 0 0 4px rgba(0, 0, 0, 0.65)',
};

/** Figma 11101:36302 — info button with ring + gradient i (11101:36305) */
function InfoButton({ onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label="More information"
      sx={{
        position: 'absolute',
        top: 10,
        right: 12,
        zIndex: 2,
        width: 20,
        height: 20,
        p: 0,
        border: 0,
        borderRadius: '100px',
        cursor: 'pointer',
        bgcolor: 'transparent',
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        src={rewardInfoButtonSrc}
        alt=""
        sx={{
          width: 20,
          height: 20,
          display: 'block',
        }}
      />
    </Box>
  );
}

function RewardValuePill({ amount, currency = 'Rupees' }) {
  return (
    <Box sx={pillSx}>
      <Box sx={{ position: 'relative', width: 33, height: 24, overflow: 'hidden', flexShrink: 0 }}>
        <Box
          component="img"
          src={rewardCouponIconSrc}
          alt=""
          sx={{
            position: 'absolute',
            width: '112.28%',
            height: '154.45%',
            left: '-7.79%',
            top: '-26.7%',
            maxWidth: 'none',
            display: 'block',
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: 16,
            lineHeight: 1,
            color: '#fff',
          }}
        >
          {amount}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: 10,
            lineHeight: 1,
            color: '#fff',
          }}
        >
          {currency}
        </Typography>
      </Box>
    </Box>
  );
}

function RevealPrizeButton({ onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        position: 'relative',
        border: '1px solid #ffc5f7',
        borderRadius: '12px',
        minHeight: 38,
        minWidth: 125,
        px: 2,
        cursor: 'pointer',
        background: 'linear-gradient(270deg, #ff00f2 0%, #822aff 100%)',
        boxShadow: '0 4px 0 rgba(0, 0, 0, 0.64)',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          boxShadow:
            'inset 2px 2px 5px rgba(255, 255, 255, 0.35), inset -2px -2px 6px rgba(0, 0, 0, 0.25)',
          pointerEvents: 'none',
        },
      }}
    >
      <Typography
        sx={{
          position: 'relative',
          zIndex: 1,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: 16,
          lineHeight: '22px',
          color: '#fff',
          whiteSpace: 'nowrap',
        }}
      >
        Reveal Prize
      </Typography>
    </Box>
  );
}

/** Figma 11101:36321 / 36300 / 36280 / 36345 — Rapchik reward row cards */
export default function RapchikRewardCard({
  theme = 'welcome',
  title,
  dateLabel,
  rank,
  leftVariant = 'wheel',
  leftLabel,
  leftValue,
  prizeAmount,
  prizeCurrency = 'Rupees',
  showRevealPrize = false,
  onInfoClick,
  onRevealPrize,
}) {
  const palette = CARD_THEMES[theme] || CARD_THEMES.welcome;
  const leftConfig = LEFT_VARIANTS[leftVariant] || LEFT_VARIANTS.wheel;
  const leftText = leftVariant === 'wheel' ? leftLabel : leftValue;
  const medalSrc = rank ? MEDAL_BY_RANK[rank] : null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 745,
        mx: 'auto',
        pt: medalSrc ? 2.5 : 0,
      }}
    >
      {medalSrc ? (
        <Box
          component="img"
          src={medalSrc}
          alt={rank ? `Rank ${rank}` : ''}
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 40,
            height: 40,
            zIndex: 3,
            filter: 'drop-shadow(0 4px 2px rgba(0, 0, 0, 0.36))',
          }}
        />
      ) : null}

      <Box
        sx={{
          borderRadius: '22px',
          border: `0.5px solid ${palette.outerBorder}`,
          background: palette.outerGradient,
          p: '5px',
        }}
      >
        <Box
          sx={{
            border: '1px dashed #fff',
            borderRadius: '18px',
            p: '5px',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              borderRadius: '12px',
              border: `0.4px solid ${palette.innerBorder}`,
              bgcolor: palette.innerBg,
              boxShadow: 'inset -2px -2px 15px rgba(105, 0, 0, 0.5)',
              minHeight: 106,
              pt: 5,
              pb: 1.5,
              px: 1.5,
            }}
          >
            <InfoButton onClick={onInfoClick} />

            <Box
              sx={{
                position: 'absolute',
                top: 1,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 'min(246px, 72%)',
                minHeight: 47,
                borderRadius: '0 0 16px 16px',
                border: `0.5px solid ${palette.headerBorder}`,
                bgcolor: 'rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1.5,
                py: 0.75,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: '20px',
                  letterSpacing: '0.098px',
                  color: '#fff',
                }}
              >
                {title}
              </Typography>
              {dateLabel ? (
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: 12,
                    lineHeight: '17px',
                    color: 'rgba(255, 255, 255, 0.6)',
                  }}
                >
                  {dateLabel}
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={{
                mt: 4.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={pillSx}>
                <Box
                  sx={{
                    position: 'relative',
                    width: leftConfig.iconBox,
                    height: leftConfig.iconBox,
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={leftConfig.icon}
                    alt=""
                    sx={{
                      position: 'absolute',
                      maxWidth: 'none',
                      display: 'block',
                      ...leftConfig.imageSx,
                    }}
                  />
                </Box>
                {leftText ? (
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: leftVariant === 'timer' ? 18 : 20,
                      lineHeight: 1.2,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {leftText}
                  </Typography>
                ) : null}
              </Box>

              {showRevealPrize ? (
                <RevealPrizeButton onClick={onRevealPrize} />
              ) : prizeAmount != null ? (
                <RewardValuePill amount={prizeAmount} currency={prizeCurrency} />
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
